import React, { useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Check, Copy, Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CouponPayload } from '../types';

/** The coupon a notification carries: code with copy, expiry and live status. */
export const CouponCard: React.FC<{ payload: CouponPayload }> = ({ payload }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const usable = !payload.status || payload.status === 'UNUSED';
  const discount = payload.discountLabel?.[i18n.language === 'vi' ? 'vi' : 'en'];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked: the code stays selectable on screen.
    }
  };

  return (
    <Box className="my-4 rounded-xl border border-dashed border-emerald-400 bg-emerald-50 p-4 dark:border-emerald-500/50 dark:bg-emerald-500/10">
      <Box className="flex flex-wrap items-center justify-between gap-3">
        <Box className="flex min-w-0 items-center gap-3">
          <Ticket className="shrink-0 text-emerald-600 dark:text-emerald-300" size={22} />
          <Box className="min-w-0">
            <Typography className="select-all break-all font-mono text-lg font-semibold tracking-wider text-gray-900 dark:text-slate-100">
              {payload.code}
            </Typography>
            <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
              {discount ? `${discount} · ` : ''}
              {payload.expiresAt
                ? t('notifications.coupon.validUntil', 'Valid until {{date}}', {
                    date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                      new Date(payload.expiresAt)
                    ),
                  })
                : t('notifications.coupon.noExpiry', 'No expiry')}
            </Typography>
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {!usable && (
            <Chip
              size="small"
              color="default"
              label={t(`notifications.coupon.status.${payload.status}`, payload.status ?? '')}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
            onClick={() => void copy()}
            className="normal-case"
          >
            {copied
              ? t('notifications.coupon.copied', 'Copied')
              : t('notifications.coupon.copy', 'Copy')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
